import type { ReactNode } from "react";
import { useEffect, useState } from "react";

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function Field({
  label,
  children,
  help,
}: {
  label: string;
  children: ReactNode;
  help?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
      {help && <p className="mt-1 text-xs text-slate-500">{help}</p>}
    </label>
  );
}

/** Like Field, but div-based — for radio groups whose options are labels themselves. */
export function FieldGroup({
  label,
  children,
  help,
}: {
  label: string;
  children: ReactNode;
  help?: string;
}) {
  return (
    <div>
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
      {help && <p className="mt-1 text-xs text-slate-500">{help}</p>}
    </div>
  );
}

/**
 * Small indicator that fades out 3 seconds after `savedAt` flips.
 */
export function useSavedFlash(savedAt: number | null): boolean {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!savedAt) return;
    setShow(true);
    const t = setTimeout(() => setShow(false), 3000);
    return () => clearTimeout(t);
  }, [savedAt]);
  return show;
}

export function SaveBar({
  saving,
  savedAt,
  error,
}: {
  saving: boolean;
  savedAt: number | null;
  error: string | null;
}) {
  const flash = useSavedFlash(savedAt);
  return (
    <>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {saving ? "Speichern…" : "Speichern"}
        </button>
        {flash && <span className="text-xs text-emerald-600">Gespeichert.</span>}
      </div>
    </>
  );
}
